// ===========================================
// BUSTED! BLACKJACK - STORE PAGE
// ===========================================
// Browse and purchase cosmetics

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { api, CosmeticItem } from '../api/client';
import { formatBalance } from '../utils/format';
import bustedLogo from '../assets/busted.png';

// Category display names and icons
const CATEGORY_INFO: Record<string, { name: string; icon: string }> = {
  'table': { name: 'Table Felts', icon: '🎲' },
  'rail': { name: 'Table Rails', icon: '🪵' },
  'shoe': { name: 'Card Shoes', icon: '👟' },
  'tray': { name: 'Chip Trays', icon: '🗃️' },
  'card-back': { name: 'Card Backs', icon: '🃏' },
  'face-cards': { name: 'Face Cards', icon: '👑' },
  'chip-set': { name: 'Chip Sets', icon: '🪙' },
  'placemat': { name: 'Placemats', icon: '🎯' },
  'avatar-frame': { name: 'Avatar Frames', icon: '🖼️' },
};

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'popular', label: 'Most Popular' },
];

export function StorePage() {
  const navigate = useNavigate();
  const { user, fetchUser } = useAuthStore();
  const [cosmetics, setCosmetics] = useState<CosmeticItem[]>([]);
  const [categories, setCategories] = useState<{ category: string; count: number }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState<{
    cosmetic: CosmeticItem;
  } | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState<{
    cosmetic: CosmeticItem;
    newBalance: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      const result = await api.cosmetics.getCategories();
      if (result.success && result.data) {
        setCategories(result.data.categories);
      }
    };
    fetchCategories();
  }, []);

  // Fetch cosmetics when filters change
  useEffect(() => {
    const fetchCosmetics = async () => {
      setIsLoading(true);
      setError(null);

      const result = await api.cosmetics.getStore({
        category: selectedCategory || undefined,
        sort: sortBy,
        search: searchQuery || undefined,
      });

      if (result.success && result.data) {
        setCosmetics(result.data.cosmetics);
      } else {
        setError('Failed to load cosmetics');
      }
      setIsLoading(false);
    };

    // Debounce search
    const timer = setTimeout(fetchCosmetics, searchQuery ? 300 : 0);
    return () => clearTimeout(timer);
  }, [selectedCategory, sortBy, searchQuery]);

  const handlePurchase = async (cosmetic: CosmeticItem) => {
    if (purchasingId) return;

    setPurchasingId(cosmetic.id);
    setShowPurchaseModal(null);

    const result = await api.cosmetics.purchase(cosmetic.id);

    if (result.success && result.data) {
      // Update the cosmetics list to mark as owned
      setCosmetics(prev =>
        prev.map(c => c.id === cosmetic.id ? { ...c, owned: true } : c)
      );

      // Refresh user balance
      fetchUser();

      // Show success modal
      setShowSuccessModal({
        cosmetic,
        newBalance: result.data.newBalance,
      });
    } else {
      setError(result.error?.message || 'Purchase failed');
    }

    setPurchasingId(null);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Purchase Confirmation Modal */}
      {showPurchaseModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Confirm Purchase</h2>

            <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-4">
                {showPurchaseModal.cosmetic.previewImageUrl ? (
                  <img
                    src={showPurchaseModal.cosmetic.previewImageUrl}
                    alt={showPurchaseModal.cosmetic.name}
                    className="w-20 h-20 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-gray-600 flex items-center justify-center text-3xl">
                    {CATEGORY_INFO[showPurchaseModal.cosmetic.category]?.icon || '🎁'}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-white">{showPurchaseModal.cosmetic.name}</p>
                  <p className="text-sm text-gray-400">
                    {CATEGORY_INFO[showPurchaseModal.cosmetic.category]?.name || showPurchaseModal.cosmetic.category}
                  </p>
                  <p className="text-casino-gold-400 font-bold mt-1">
                    {formatBalance(showPurchaseModal.cosmetic.priceAmount)}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between text-sm mb-4">
              <span className="text-gray-400">Your Balance</span>
              <span className="text-white">{formatBalance(user?.balance ?? 0)}</span>
            </div>
            <div className="flex justify-between text-sm mb-6">
              <span className="text-gray-400">After Purchase</span>
              <span className={((user?.balance ?? 0) - showPurchaseModal.cosmetic.priceAmount) < 0 ? 'text-red-400' : 'text-green-400'}>
                {formatBalance((user?.balance ?? 0) - showPurchaseModal.cosmetic.priceAmount)}
              </span>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPurchaseModal(null)}
                className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handlePurchase(showPurchaseModal.cosmetic)}
                disabled={(user?.balance ?? 0) < showPurchaseModal.cosmetic.priceAmount}
                className="flex-1 py-3 bg-casino-gold-500 hover:bg-casino-gold-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 font-bold rounded-xl transition-colors"
              >
                Purchase
              </button>
            </div>

            <p className="text-gray-600 text-[10px] mt-4 text-center leading-snug">
              Purchases use virtual B-Bucks. Items and chips have no cash value and are non-refundable and non-redeemable.
            </p>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 max-w-md mx-4 text-center">
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-white mb-2">Purchase Complete!</h2>
            <p className="text-gray-400 mb-4">
              You now own <span className="text-casino-gold-400">{showSuccessModal.cosmetic.name}</span>
            </p>

            <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
              <p className="text-gray-400 text-sm">New Balance</p>
              <p className="text-2xl font-bold text-white">
                {formatBalance(showSuccessModal.newBalance)}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSuccessModal(null)}
                className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition-colors"
              >
                Keep Shopping
              </button>
              <button
                onClick={() => navigate('/locker')}
                className="flex-1 py-3 bg-casino-gold-500 hover:bg-casino-gold-400 text-gray-900 font-bold rounded-xl transition-colors"
              >
                View in Locker
              </button>
            </div>

            <p className="text-gray-600 text-[10px] mt-4 leading-snug">
              Virtual items purchased with B-Bucks have no cash value and cannot be redeemed for money.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={bustedLogo} alt="BUSTED!" className="h-[50px]" />
              <div>
                <h1 className="text-xl font-bold text-white">Cosmetic Store</h1>
                <p className="text-sm text-gray-400">Personalize your table</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="px-4 py-2 bg-green-600 rounded-lg">
                <span className="text-white font-bold">{formatBalance(user?.balance ?? 0)}</span>
              </div>
              <button
                onClick={() => navigate('/locker')}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
              >
                My Locker
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Home
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters Row */}
        <div className="flex flex-wrap gap-4 mb-6">
          {/* Category Pills */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                !selectedCategory
                  ? 'bg-casino-gold-500 text-gray-900'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              All ({categories.reduce((sum, c) => sum + c.count, 0)})
            </button>
            {categories.map(cat => (
              <button
                key={cat.category}
                onClick={() => setSelectedCategory(cat.category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat.category
                    ? 'bg-casino-gold-500 text-gray-900'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {CATEGORY_INFO[cat.category]?.icon} {CATEGORY_INFO[cat.category]?.name || cat.category} ({cat.count})
              </button>
            ))}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Search */}
          <input
            type="text"
            placeholder="Search cosmetics..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-casino-gold-500 w-64"
          />

          {/* Sort */}
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-casino-gold-500"
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200">
            {error}
            <button onClick={() => setError(null)} className="ml-4 text-red-400 hover:text-red-300">
              Dismiss
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg">Loading cosmetics...</div>
          </div>
        ) : cosmetics.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg">No cosmetics found</div>
            <p className="text-gray-500 mt-2">Try adjusting your filters</p>
          </div>
        ) : (
          /* Cosmetics Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {cosmetics.map(cosmetic => (
              <div
                key={cosmetic.id}
                className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden hover:border-casino-gold-500/50 transition-colors"
              >
                {/* Preview Image */}
                <div className="aspect-square bg-gray-700 relative">
                  {cosmetic.previewImageUrl ? (
                    <img
                      src={cosmetic.previewImageUrl}
                      alt={cosmetic.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-6xl">
                      {CATEGORY_INFO[cosmetic.category]?.icon || '🎁'}
                    </div>
                  )}

                  {/* Owned Badge */}
                  {cosmetic.owned && (
                    <div className="absolute top-2 right-2 px-2 py-1 bg-green-600 text-white text-xs font-bold rounded">
                      OWNED
                    </div>
                  )}

                  {/* Free Badge */}
                  {cosmetic.priceAmount === 0 && !cosmetic.owned && (
                    <div className="absolute top-2 right-2 px-2 py-1 bg-casino-gold-500 text-gray-900 text-xs font-bold rounded">
                      FREE
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="text-xs text-gray-500 mb-1">
                    {CATEGORY_INFO[cosmetic.category]?.name || cosmetic.category}
                  </div>
                  <h3 className="font-semibold text-white mb-1">{cosmetic.name}</h3>
                  {cosmetic.description && (
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2">{cosmetic.description}</p>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-casino-gold-400 font-bold">
                      {cosmetic.priceAmount === 0 ? 'Free' : formatBalance(cosmetic.priceAmount)}
                    </span>

                    {cosmetic.owned ? (
                      <button
                        onClick={() => navigate('/locker')}
                        className="px-3 py-1.5 bg-gray-700 text-gray-300 text-sm rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        Equip
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowPurchaseModal({ cosmetic })}
                        disabled={purchasingId === cosmetic.id}
                        className="px-3 py-1.5 bg-casino-gold-500 hover:bg-casino-gold-400 text-gray-900 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                      >
                        {purchasingId === cosmetic.id ? '...' : 'Buy'}
                      </button>
                    )}
                  </div>

                  {/* Owner count */}
                  {cosmetic.ownerCount !== undefined && cosmetic.ownerCount > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      {cosmetic.ownerCount.toLocaleString()} player{cosmetic.ownerCount !== 1 ? 's' : ''} own this
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default StorePage;
